import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { DemIntegrationConfig } from '../../types/dem';
import { DEM_MASKED_SECRET, fetchDemIntegrationConfig } from '../../lib/demSettings';
import { fetchCartrackConnection } from '../../lib/cartrack';
import { FLEET_FEATURE_ENABLED } from '../../lib/fleetFeature';
import {
  fetchLalamoveApiStatus,
  type LalamoveApiStatus,
} from '../../lib/deliveryArrangements';
import { getGoogleMapsApiKey } from '../../lib/googleMaps';
import { isWebPushConfigured } from '../../lib/webPush';
import { Modal } from '../ui/Modal';
import { DemLalamoveWebhookPanel } from './DemLalamoveWebhookPanel';

type FieldSource = 'server' | 'app-build' | 'edge' | 'build';

type EffectiveField = {
  value: string;
  source: FieldSource | null;
  buildPlaintext?: string;
};

type IntegrationFieldDef = {
  key: string;
  label: string;
  field: EffectiveField;
  revealedValue?: string;
  secret?: boolean;
};

type IntegrationItem = {
  id: string;
  title: string;
  note: ReactNode;
  summary: string;
  status: 'ready' | 'partial' | 'missing';
  sourceLabel: string;
  fields: IntegrationFieldDef[];
};

type IntegrationCategory = {
  id: string;
  name: string;
  items: IntegrationItem[];
};

type DemSettingsConfigFieldProps = {
  label: string;
  value: string;
  revealedValue?: string;
  buildPlaintext?: string;
  source: FieldSource | null;
  secret?: boolean;
  canReveal: boolean;
  revealing: boolean;
  onRevealRequest: () => Promise<void>;
};

function EyeIcon({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
        <path
          fill="currentColor"
          d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 12a5 5 0 110-10 5 5 0 010 10zm0-8a3 3 0 100 6 3 3 0 000-6z"
        />
        <path fill="currentColor" d="M3.2 3.2l17.6 17.6-1.4 1.4L1.8 4.6z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
      <path
        fill="currentColor"
        d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 12a5 5 0 110-10 5 5 0 010 10zm0-8a3 3 0 100 6 3 3 0 000-6z"
      />
    </svg>
  );
}

function sourceBadgeLabel(source: FieldSource | null): string {
  if (source === 'server') return 'Server';
  if (source === 'app-build' || source === 'build') return 'App build';
  if (source === 'edge') return 'Edge Function';
  return '';
}

function effectiveField(
  serverValue: string,
  buildValue: string,
  secret: boolean,
): EffectiveField {
  const server = serverValue.trim();
  const build = buildValue.trim();
  if (server) {
    return {
      value: secret && server !== DEM_MASKED_SECRET ? DEM_MASKED_SECRET : server,
      source: 'server',
    };
  }
  if (build) {
    return {
      value: secret ? DEM_MASKED_SECRET : build,
      source: 'app-build',
      buildPlaintext: build,
    };
  }
  return { value: '', source: null };
}

function fieldConfigured(field: EffectiveField): boolean {
  return Boolean(field.source);
}

function configStatus(fields: EffectiveField[]): 'ready' | 'partial' | 'missing' {
  const setCount = fields.filter(fieldConfigured).length;
  if (setCount === 0) return 'missing';
  if (setCount === fields.length) return 'ready';
  return 'partial';
}

function statusLabel(status: 'ready' | 'partial' | 'missing'): string {
  if (status === 'ready') return 'Configured';
  if (status === 'partial') return 'Partial';
  return 'Not set';
}

function dominantSourceLabel(fields: EffectiveField[]): string {
  const sources = new Set(
    fields.map((f) => f.source).filter((s): s is FieldSource => s != null),
  );
  if (sources.size === 0) return '—';
  if (sources.size === 1) return sourceBadgeLabel([...sources][0]!) || '—';
  return 'Mixed';
}

function DemSettingsConfigField({
  label,
  value,
  revealedValue,
  buildPlaintext,
  source,
  secret = false,
  canReveal,
  revealing,
  onRevealRequest,
}: DemSettingsConfigFieldProps) {
  const [open, setOpen] = useState(false);
  const trimmed = value.trim();
  const revealedTrimmed = (revealedValue ?? '').trim();
  const isMaskedLiteral = trimmed === DEM_MASKED_SECRET;
  const unset = !trimmed && !source;

  let displayValue: string;
  if (open && canReveal) {
    if (source === 'app-build' || source === 'build') {
      displayValue = (buildPlaintext ?? '').trim() || 'Not set';
    } else if (source === 'edge') {
      displayValue = revealedTrimmed || 'Stored in Edge Function secrets (not readable here)';
    } else if (revealedValue == null && revealing) {
      displayValue = DEM_MASKED_SECRET;
    } else {
      const actual = revealedTrimmed;
      displayValue = !actual || actual === DEM_MASKED_SECRET ? 'Not set' : actual;
    }
  } else if (unset) {
    displayValue = 'Not set';
  } else if (secret || isMaskedLiteral) {
    displayValue = DEM_MASKED_SECRET;
  } else {
    displayValue = trimmed;
  }

  const showEye =
    canReveal &&
    displayValue === DEM_MASKED_SECRET &&
    source !== 'edge';

  const handleReveal = async () => {
    if (!showEye) return;
    if (source === 'server' && revealedValue == null) {
      await onRevealRequest();
    }
    setOpen(true);
  };

  const src = sourceBadgeLabel(source);

  return (
    <label className="dem-settings-field dem-settings-field--block dem-settings-field--compact">
      <span className="dem-settings-field__label-row">
        <span>{label}</span>
        {src ? (
          <span className={`dem-settings-source dem-settings-source--${source === 'build' ? 'app-build' : source}`}>
            {src}
          </span>
        ) : null}
      </span>
      <div className="dem-settings-secret-row">
        <input
          className={`dem-settings-input dem-settings-input--readonly${
            displayValue === 'Not set' ? ' dem-settings-input--unset' : ''
          }`}
          readOnly
          value={displayValue}
          type="text"
          autoComplete="off"
          spellCheck={false}
        />
        {showEye ? (
          <button
            type="button"
            className="dem-settings-eye-btn"
            aria-label={`Show ${label}`}
            title="Show value (IT only)"
            disabled={revealing && source === 'server'}
            onClick={(e) => {
              e.stopPropagation();
              void handleReveal();
            }}
          >
            <EyeIcon open={false} />
          </button>
        ) : null}
      </div>
    </label>
  );
}

type DemSettingsIntegrationPanelProps = {
  config: DemIntegrationConfig;
  showFullIntegration: boolean;
  canRevealSecrets?: boolean;
  actorEmail?: string;
  refreshing?: boolean;
  onRefresh?: () => void;
};

export function DemSettingsIntegrationPanel({
  config,
  showFullIntegration,
  canRevealSecrets = false,
  actorEmail = '',
  refreshing = false,
  onRefresh,
}: DemSettingsIntegrationPanelProps) {
  const [revealedConfig, setRevealedConfig] = useState<DemIntegrationConfig | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [cartrack, setCartrack] = useState<{
    connected: boolean;
    baseUrl: string;
    error?: string;
  } | null>(null);
  const [lalamove, setLalamove] = useState<LalamoveApiStatus | null>(null);
  const [liveLoading, setLiveLoading] = useState(true);

  const buildUrl = import.meta.env.VITE_SUPABASE_URL?.trim() ?? '';
  const buildAnon = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim() ?? '';
  const buildCtId = import.meta.env.VITE_CT_API_ID?.trim() ?? '';
  const buildCtSecret = import.meta.env.VITE_CT_API_SECRET?.trim() ?? '';
  const googleMapsKey = getGoogleMapsApiKey();
  const webPushOn = isWebPushConfigured();
  const vapidPublic = (import.meta.env.VITE_VAPID_PUBLIC_KEY ?? '').trim();

  const ensureRevealed = useCallback(async () => {
    if (revealedConfig || !canRevealSecrets) return;
    setRevealing(true);
    setRevealError(null);
    try {
      const data = await fetchDemIntegrationConfig({
        reveal: true,
        actorEmail,
      });
      setRevealedConfig(data);
    } catch (e) {
      setRevealError(e instanceof Error ? e.message : 'Failed to reveal secrets.');
      throw e;
    } finally {
      setRevealing(false);
    }
  }, [actorEmail, canRevealSecrets, revealedConfig]);

  const loadLiveStatus = useCallback(async () => {
    setLiveLoading(true);
    try {
      const [ct, ll] = await Promise.all([
        FLEET_FEATURE_ENABLED
          ? fetchCartrackConnection()
              .then((row) => ({ connected: row.connected, baseUrl: row.baseUrl }))
              .catch((err) => ({
                connected: false,
                baseUrl: '',
                error: err instanceof Error ? err.message : 'Cartrack unavailable',
              }))
          : Promise.resolve({
              connected: false,
              baseUrl: '',
              error: 'Fleet temporarily disabled',
            }),
        fetchLalamoveApiStatus(),
      ]);
      setCartrack(ct);
      setLalamove(ll);
    } finally {
      setLiveLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadLiveStatus();
  }, [loadLiveStatus]);

  const categories = useMemo((): IntegrationCategory[] => {
    const revealed = revealedConfig;
    const supabaseUrl = effectiveField(config.supabase.url, buildUrl, false);
    const supabaseAnon = effectiveField(config.supabase.anon_key, buildAnon, true);
    const supabaseService = effectiveField(config.supabase.service_key, '', true);
    const gemini = effectiveField(config.gemini.api_key, '', true);
    const m365Email = effectiveField(config.m365.target_email, '', false);
    const m365Tenant = effectiveField(config.m365.tenant_id, '', true);
    const m365Client = effectiveField(config.m365.client_id, '', true);
    const m365Secret = effectiveField(config.m365.client_secret, '', true);
    const ctId = effectiveField(config.cloudtalk.api_id, buildCtId, true);
    const ctSecret = effectiveField(config.cloudtalk.api_secret, buildCtSecret, true);

    const googleField: EffectiveField = googleMapsKey
      ? { value: DEM_MASKED_SECRET, source: 'app-build', buildPlaintext: googleMapsKey }
      : { value: '', source: null };
    const vapidField: EffectiveField = webPushOn
      ? { value: DEM_MASKED_SECRET, source: 'app-build', buildPlaintext: vapidPublic }
      : { value: '', source: null };

    const cartrackConnected = Boolean(cartrack?.connected);
    const cartrackFields: IntegrationFieldDef[] = [
      {
        key: 'status',
        label: 'Connection',
        field: {
          value: liveLoading
            ? 'Checking…'
            : cartrackConnected
              ? 'Connected'
              : cartrack?.error || 'Not connected',
          source: cartrackConnected ? 'edge' : null,
        },
      },
      {
        key: 'base',
        label: 'API base URL',
        field: {
          value: cartrack?.baseUrl || 'https://fleetapi-sg.cartrack.com',
          source: cartrackConnected ? 'edge' : null,
        },
      },
      {
        key: 'user',
        label: 'API username',
        field: {
          value: cartrackConnected ? DEM_MASKED_SECRET : '',
          source: cartrackConnected ? 'edge' : null,
        },
        secret: true,
      },
      {
        key: 'pass',
        label: 'API password',
        field: {
          value: cartrackConnected ? DEM_MASKED_SECRET : '',
          source: cartrackConnected ? 'edge' : null,
        },
        secret: true,
      },
    ];

    const lalamoveConnected = Boolean(lalamove?.connected);
    const lalamoveFields: IntegrationFieldDef[] = [
      {
        key: 'status',
        label: 'Connection',
        field: {
          value: liveLoading
            ? 'Checking…'
            : lalamoveConnected
              ? 'Connected'
              : lalamove?.error || 'Not connected',
          source: lalamoveConnected ? 'edge' : null,
        },
      },
      {
        key: 'env',
        label: 'Environment',
        field: {
          value: liveLoading ? '…' : lalamove?.sandbox ? 'Sandbox' : 'Production',
          source: lalamoveConnected ? 'edge' : null,
        },
      },
      {
        key: 'balance',
        label: 'Wallet balance',
        field: {
          value:
            lalamove?.balance != null && Number.isFinite(lalamove.balance)
              ? String(lalamove.balance)
              : '—',
          source: lalamoveConnected ? 'edge' : null,
        },
      },
      {
        key: 'key',
        label: 'API key',
        field: {
          value: lalamoveConnected ? DEM_MASKED_SECRET : '',
          source: lalamoveConnected ? 'edge' : null,
        },
        secret: true,
      },
      {
        key: 'secret',
        label: 'API secret',
        field: {
          value: lalamoveConnected ? DEM_MASKED_SECRET : '',
          source: lalamoveConnected ? 'edge' : null,
        },
        secret: true,
      },
    ];

    function itemFromFields(
      id: string,
      title: string,
      note: ReactNode,
      summary: string,
      fields: IntegrationFieldDef[],
      overrides?: Partial<Pick<IntegrationItem, 'status' | 'sourceLabel'>>,
    ): IntegrationItem {
      const values = fields.map((f) => f.field);
      return {
        id,
        title,
        note,
        summary,
        status: overrides?.status ?? configStatus(values),
        sourceLabel:
          overrides?.sourceLabel ??
          `${dominantSourceLabel(values)} · ${values.filter(fieldConfigured).length}/${values.length} keys`,
        fields,
      };
    }

    return [
      {
        id: 'core',
        name: 'Core platform',
        items: [
          itemFromFields(
            'supabase',
            'Supabase database',
            <>
              Portal login uses <strong>App build</strong> (<code>VITE_SUPABASE_*</code>). Service key is
              required on the server for PHP proxies.
            </>,
            supabaseUrl.source ? supabaseUrl.value || 'URL configured' : 'No Supabase URL configured',
            [
              {
                key: 'url',
                label: 'Supabase URL',
                field: supabaseUrl,
                revealedValue: revealed?.supabase.url,
              },
              {
                key: 'anon',
                label: 'Anon key',
                field: supabaseAnon,
                revealedValue: revealed?.supabase.anon_key,
                secret: true,
              },
              {
                key: 'service',
                label: 'Service key',
                field: supabaseService,
                revealedValue: revealed?.supabase.service_key,
                secret: true,
              },
            ],
          ),
          itemFromFields(
            'gemini',
            'Gemini AI engine',
            <>
              Used by server PO extract and weekly AI summary (
              <code>order-email-secrets.local.php</code>).
            </>,
            gemini.source ? 'API key configured' : 'No API key configured',
            [
              {
                key: 'api',
                label: 'API key',
                field: gemini,
                revealedValue: revealed?.gemini.api_key,
                secret: true,
              },
            ],
          ),
          itemFromFields(
            'm365',
            'Microsoft 365 email',
            <>
              Used by server email / Graph proxies (<code>order-email-secrets.local.php</code>).
            </>,
            m365Email.source
              ? m365Email.value || 'Target email configured'
              : 'No target email configured',
            [
              {
                key: 'email',
                label: 'Target email',
                field: m365Email,
                revealedValue: revealed?.m365.target_email,
              },
              {
                key: 'tenant',
                label: 'Tenant ID',
                field: m365Tenant,
                revealedValue: revealed?.m365.tenant_id,
                secret: true,
              },
              {
                key: 'client',
                label: 'Client ID',
                field: m365Client,
                revealedValue: revealed?.m365.client_id,
                secret: true,
              },
              {
                key: 'secret',
                label: 'Client secret',
                field: m365Secret,
                revealedValue: revealed?.m365.client_secret,
                secret: true,
              },
            ],
          ),
        ],
      },
      {
        id: 'ops',
        name: 'DEM & delivery',
        items: [
          itemFromFields(
            'cloudtalk',
            'CloudTalk',
            <>
              Live missed calls and agent status come from the <strong>CloudTalk webhook</strong>
              (Supabase <code>cloudtalk-webhook</code>). API keys are not used for the CloudTalk page.
            </>,
            ctId.source || ctSecret.source
              ? 'API credentials configured'
              : 'No CloudTalk credentials configured',
            [
              {
                key: 'id',
                label: 'API key ID',
                field: ctId,
                revealedValue: revealed?.cloudtalk.api_id,
                secret: true,
              },
              {
                key: 'secret',
                label: 'API secret',
                field: ctSecret,
                revealedValue: revealed?.cloudtalk.api_secret,
                secret: true,
              },
            ],
          ),
          itemFromFields(
            'lalamove',
            'Lalamove',
            <>
              DEM deliveries. Keys live in Supabase Edge Function secrets (
              <code>LALAMOVE_API_KEY</code> / <code>LALAMOVE_API_SECRET</code>).
            </>,
            liveLoading
              ? 'Checking connection…'
              : lalamoveConnected
                ? lalamove?.sandbox
                  ? 'Connected (sandbox)'
                  : 'Connected (production)'
                : lalamove?.error || 'Not connected',
            lalamoveFields,
            {
              status: liveLoading ? 'partial' : lalamoveConnected ? 'ready' : 'missing',
              sourceLabel: lalamoveConnected
                ? `Edge Function · ${lalamove?.sandbox ? 'Sandbox' : 'Production'}`
                : 'Edge Function · —',
            },
          ),
        ],
      },
      {
        id: 'maps',
        name: 'Maps & fleet',
        items: [
          itemFromFields(
            'google-maps',
            'Google Maps',
            <>
              Fleet and delivery maps. App build key <code>VITE_GOOGLE_MAPS_API_KEY</code>.
            </>,
            googleMapsKey ? 'API key configured in this build' : 'No Google Maps key in this build',
            [
              {
                key: 'api',
                label: 'API key',
                field: googleField,
                secret: true,
              },
            ],
          ),
          itemFromFields(
            'cartrack',
            'Cartrack',
            <>
              Fleet GPS. Credentials live in Supabase Edge Function secrets (
              <code>CARTRACK_API_USERNAME</code> / <code>CARTRACK_API_PASSWORD</code>).
              {!FLEET_FEATURE_ENABLED ? (
                <>
                  {' '}
                  <strong>Fleet is temporarily disabled</strong> — no live GPS polling.
                </>
              ) : null}
            </>,
            !FLEET_FEATURE_ENABLED
              ? 'Temporarily disabled'
              : liveLoading
                ? 'Checking connection…'
                : cartrackConnected
                  ? cartrack?.baseUrl || 'Connected'
                  : cartrack?.error || 'Not connected',
            cartrackFields,
            {
              status: !FLEET_FEATURE_ENABLED
                ? 'missing'
                : liveLoading
                  ? 'partial'
                  : cartrackConnected
                    ? 'ready'
                    : 'missing',
              sourceLabel: !FLEET_FEATURE_ENABLED
                ? 'Disabled'
                : cartrackConnected
                  ? 'Edge Function · Connected'
                  : 'Edge Function · —',
            },
          ),
        ],
      },
      {
        id: 'notify',
        name: 'Notifications',
        items: [
          itemFromFields(
            'web-push',
            'Web Push',
            <>
              Browser push. Public key in App build (<code>VITE_VAPID_PUBLIC_KEY</code>); private key
              in Edge Function (<code>VAPID_PRIVATE_KEY</code>).
            </>,
            webPushOn ? 'VAPID public key configured' : 'No VAPID public key in this build',
            [
              {
                key: 'public',
                label: 'VAPID public key',
                field: vapidField,
                secret: true,
              },
              {
                key: 'private',
                label: 'VAPID private key',
                field: {
                  value: webPushOn ? DEM_MASKED_SECRET : '',
                  source: webPushOn ? 'edge' : null,
                },
                secret: true,
              },
            ],
            {
              status: webPushOn ? 'ready' : 'missing',
              sourceLabel: webPushOn ? 'App build + Edge' : '—',
            },
          ),
        ],
      },
    ];
  }, [
    buildAnon,
    buildCtId,
    buildCtSecret,
    buildUrl,
    cartrack,
    config,
    googleMapsKey,
    lalamove,
    liveLoading,
    revealedConfig,
    vapidPublic,
    webPushOn,
  ]);

  if (!showFullIntegration) {
    return null;
  }

  const flatItems = categories.flatMap((cat) => cat.items);
  const active = flatItems.find((item) => item.id === activeId) ?? null;
  const totalCount = flatItems.length;

  return (
    <>
      <section className="card settings-panel integration-settings-panel">
        <div className="settings-panel__head unified-users-panel__head">
          <div className="unified-users-panel__title-row">
            <h2 className="settings-panel__title">Integrations</h2>
            <div className="unified-users-panel__actions">
              <span className="settings-panel__count">
                {totalCount} {totalCount === 1 ? 'service' : 'services'}
              </span>
              {onRefresh ? (
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => {
                    onRefresh();
                    void loadLiveStatus();
                  }}
                  disabled={refreshing || liveLoading}
                >
                  {refreshing || liveLoading ? 'Refreshing…' : 'Refresh'}
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {revealError && !activeId ? <div className="alert alert-error">{revealError}</div> : null}

        <div className="unified-users-dept-list">
          {categories.map((category) => (
            <section key={category.id} className="unified-users-dept">
              <header className="unified-users-dept__head">
                <h3 className="unified-users-dept__title">{category.name}</h3>
                <span className="unified-users-dept__count">
                  {category.items.length}{' '}
                  {category.items.length === 1 ? 'integration' : 'integrations'}
                </span>
              </header>
              <div className="unified-users-matrix-wrap integration-matrix-wrap">
                <table className="unified-users-matrix integration-matrix">
                  <thead>
                    <tr>
                      <th scope="col" className="integration-matrix__name-col">
                        Integration
                      </th>
                      <th scope="col">Status</th>
                      <th scope="col">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {category.items.map((item) => (
                      <tr
                        key={item.id}
                        className="integration-matrix__row"
                        tabIndex={0}
                        role="button"
                        aria-label={`View ${item.title}`}
                        onClick={() => {
                          setRevealError(null);
                          setActiveId(item.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setRevealError(null);
                            setActiveId(item.id);
                          }
                        }}
                      >
                        <td className="integration-matrix__name">
                          <span className="integration-matrix__title">{item.title}</span>
                          <span className="integration-matrix__summary" title={item.summary}>
                            {item.summary}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`integration-config-status integration-config-status--${item.status}`}
                          >
                            {statusLabel(item.status)}
                          </span>
                        </td>
                        <td>
                          <span className="integration-matrix__source">{item.sourceLabel}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      </section>

      <Modal
        title={active ? active.title : 'Integration'}
        open={Boolean(active)}
        onClose={() => setActiveId(null)}
        wide
      >
        {active ? (
          <div className="integration-config-modal">
            {revealError ? <p className="alert alert-error">{revealError}</p> : null}
            <p className="field-hint integration-config-modal__note">{active.note}</p>
            <div className="integration-config-modal__fields">
              {active.fields.map((field) => (
                <DemSettingsConfigField
                  key={field.key}
                  label={field.label}
                  value={field.field.value}
                  revealedValue={field.revealedValue}
                  buildPlaintext={field.field.buildPlaintext}
                  source={field.field.source}
                  secret={field.secret}
                  canReveal={canRevealSecrets}
                  revealing={revealing}
                  onRevealRequest={ensureRevealed}
                />
              ))}
            </div>
            {active.id === 'lalamove' ? (
              <div className="integration-config-modal__webhook">
                <h3 className="settings-panel__subtitle">Webhook</h3>
                <DemLalamoveWebhookPanel compact />
              </div>
            ) : null}
            <div className="settings-sharepoint-form__actions">
              <button type="button" className="btn-secondary" onClick={() => setActiveId(null)}>
                Close
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  );
}
